/**
 * Gerenciar a escola: o que ela é, quem entra nela, e como ela acaba.
 *
 * **A tela nasceu de um erro real, e o erro ensina o desenho.** Alguém criou
 * uma segunda escola com o mesmo nome da de verdade, entrou na errada e viu
 * tela vazia. Não havia como renomear, não havia como excluir pela interface,
 * e a pessoa certa não estava na escola certa. Três buracos, uma tela.
 *
 * **Três decisões, e nenhuma é estética.**
 *
 * 1. **Excluir fica no fim e pede o nome digitado.** Não porque confirmação
 *    dupla seja virtude — ela costuma ser teatro —, mas porque aqui o erro é
 *    silencioso: a exclusão é lógica, a escola some da lista, e quem a apagou
 *    por engano não tem na interface nenhum caminho de volta. Digitar o nome
 *    é o que separa "cliquei errado" de "eu quis".
 *
 * 2. **Quem não é dono vê tudo, e os campos vêm desabilitados.** Esconder as
 *    seções faria um EDITOR concluir que a escola não tem dono nem convidados
 *    — e ele perguntaria a quem? Ver o que existe, e por que não pode mexer,
 *    é mais honesto do que uma tela que finge ser menor.
 *
 * 3. **O link do convite aparece uma vez e some.** O servidor guarda só o
 *    hash, como faz com sessão. A tela diz isso na hora, em vez de deixar a
 *    pessoa descobrir ao reabrir a lista e não achar o link.
 */

import { useCallback, useEffect, useState } from 'react'

import {
  ErroApi,
  escola as apiEscola,
  type Convite,
  type Escola as EscolaDoc,
  type Membro,
} from '../api'
import { Icon } from '../components/Icon'

const PAPEIS = [
  { valor: 'LEITOR', rotulo: 'Leitor', nota: 'vê tudo, não altera nada' },
  { valor: 'EDITOR', rotulo: 'Editor', nota: 'cadastra, importa e monta grade' },
  { valor: 'PROPRIETARIO', rotulo: 'Proprietário', nota: 'tudo isso, mais convidar e excluir' },
]

const GRAUS = [
  { valor: 'IMPOSSIVEL', rotulo: 'Impossível — nunca fora do turno' },
  { valor: 'MUITO_RUIM', rotulo: 'Muito ruim' },
  { valor: 'RUIM', rotulo: 'Ruim' },
  { valor: 'POUCO_RUIM', rotulo: 'Pouco ruim' },
  { valor: 'NEUTRO', rotulo: 'Neutro — tanto faz' },
]

const dia = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

export function Escola({
  projeto,
  aoRenomear,
  aoExcluir,
}: {
  projeto: string
  /** O nome vive na barra de cima e na troca de escola; mudou aqui, muda lá. */
  aoRenomear: () => void
  /** Depois de excluir não há escola para voltar: quem decide para onde é o App. */
  aoExcluir: () => void
}) {
  const [dados, setDados] = useState<EscolaDoc | null>(null)
  const [membros, setMembros] = useState<Membro[]>([])
  const [convites, setConvites] = useState<Convite[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const [nome, setNome] = useState('')
  const [ano, setAno] = useState(0)
  const [semanas, setSemanas] = useState('')
  const [grau, setGrau] = useState('')

  const [emailNovo, setEmailNovo] = useState('')
  const [papelNovo, setPapelNovo] = useState('EDITOR')
  const [linkDoConvite, setLinkDoConvite] = useState<string | null>(null)

  const [confirmacao, setConfirmacao] = useState('')

  const dono = dados?.papel === 'PROPRIETARIO'

  const carregar = useCallback(async () => {
    try {
      const e = await apiEscola.ler(projeto)
      setDados(e)
      setNome(e.nome)
      setAno(e.ano_letivo)
      setSemanas(String(e.semanas_no_mes))
      setGrau(e.grau_fora_do_turno)
      setErro(null)
      // Membros e convites em pedidos próprios: um LEITOR lê a lista de
      // membros, mas convite é do dono, e um 403 ali não pode derrubar o
      // resto da tela.
      setMembros(await apiEscola.membros(projeto).catch(() => []))
      setConvites(await apiEscola.convites(projeto).catch(() => []))
    } catch (x) {
      setErro(x instanceof ErroApi ? x.message : 'não foi possível abrir esta escola')
    }
  }, [projeto])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function salvar(mudanca: Record<string, unknown>, feito: string) {
    setSalvando(true)
    setErro(null)
    try {
      const e = await apiEscola.alterar(projeto, mudanca)
      setDados(e)
      setAviso(feito)
      aoRenomear()
    } catch (x) {
      setErro(x instanceof ErroApi ? x.message : 'não foi possível salvar')
    } finally {
      setSalvando(false)
    }
  }

  if (erro && !dados) {
    return (
      <div className="section">
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      </div>
    )
  }
  if (!dados) return <p className="field__hint">Carregando…</p>

  return (
    <div className="section">
      <header className="page-head">
        <h1>A escola</h1>
        <p className="page-head__note">
          {membros.length} {membros.length === 1 ? 'pessoa' : 'pessoas'} ·{' '}
          {dono ? 'você é proprietário' : 'você não pode alterar'}
        </p>
      </header>

      {!dono && (
        <p className="field__hint">
          Só quem é proprietário altera o nome, convida gente ou exclui a escola. Você vê tudo
          para saber a quem pedir.
        </p>
      )}

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}
      {aviso && (
        <p className="state state--ok" role="status">
          <Icon name="ok" size={14} />
          {aviso}
        </p>
      )}

      <div className="section__head">
        <h2>Identificação</h2>
        <p>o nome que aparece na barra de cima e na troca de escola</p>
      </div>
      <form
        className="form"
        data-tour="identificacao"
        onSubmit={(e) => {
          e.preventDefault()
          void salvar({ nome: nome.trim(), ano_letivo: ano }, 'Nome e ano salvos.')
        }}
      >
        <label className="field">
          <span className="field__label">Nome</span>
          <input
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            disabled={!dono}
            required
          />
        </label>
        <label className="field">
          <span className="field__label">Ano letivo</span>
          <input
            className="input"
            type="number"
            value={ano}
            onChange={(e) => setAno(Number(e.target.value))}
            disabled={!dono}
          />
        </label>
        {dono && (
          <div className="row">
            <button type="submit" className="btn btn--primary" disabled={salvando}>
              Salvar
            </button>
          </div>
        )}
      </form>

      <div className="section__head">
        <h2>Cálculo e montagem</h2>
        <p>dois números que mudam o que a grade e o custo dizem</p>
      </div>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault()
          void salvar(
            { semanas_no_mes: Number(semanas), grau_fora_do_turno: grau },
            'Cálculo salvo.',
          )
        }}
      >
        <label className="field">
          <span className="field__label">Semanas no mês</span>
          <input
            className="input input--mono"
            type="number"
            step="0.01"
            min="0.01"
            value={semanas}
            onChange={(e) => setSemanas(e.target.value)}
            disabled={!dono}
          />
          <span className="field__hint">
            Quantas semanas a folha conta no mês. Multiplica tudo na tela de Custo: 4,5 na ESPM,
            medido na própria folha.
          </span>
        </label>
        <label className="field">
          <span className="field__label">Aula fora do turno da turma</span>
          <select
            className="select"
            value={grau}
            onChange={(e) => setGrau(e.target.value)}
            disabled={!dono}
          >
            {GRAUS.map((g) => (
              <option key={g.valor} value={g.valor}>
                {g.rotulo}
              </option>
            ))}
          </select>
          <span className="field__hint">
            O padrão da escola. Uma oferta pode abrir exceção na ficha dela — é como o minor da
            segunda-feira cabe sem virar regra geral.
          </span>
        </label>
        {dono && (
          <div className="row">
            <button type="submit" className="btn btn--primary" disabled={salvando}>
              Salvar
            </button>
          </div>
        )}
      </form>

      <div className="section__head">
        <h2>Quem tem acesso</h2>
        <p>e o que cada papel pode fazer</p>
      </div>
      <div className="tabela-rolavel">
        <table className="tabela" data-tour="membros">
          <thead>
            <tr>
              <th>E-mail</th>
              <th>Papel</th>
              <th>Desde</th>
              {dono && <th />}
            </tr>
          </thead>
          <tbody>
            {membros.map((m) => (
              <tr key={m.id}>
                <td>{m.email}</td>
                <td>{PAPEIS.find((p) => p.valor === m.papel)?.rotulo ?? m.papel}</td>
                <td className="mono">{dia(m.criado_em)}</td>
                {dono && (
                  <td className="tabela__acoes">
                    {/* O último proprietário não sai: projeto sem dono é
                        projeto que ninguém consegue abrir depois. O servidor
                        também recusa — isto aqui só evita a viagem. */}
                    {m.papel === 'PROPRIETARIO' &&
                    membros.filter((x) => x.papel === 'PROPRIETARIO').length === 1 ? (
                      <span className="field__hint">único dono</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={async () => {
                          if (!confirm(`Tirar ${m.email} desta escola?`)) return
                          try {
                            await apiEscola.removerMembro(projeto, m.id)
                            await carregar()
                            setAviso(`${m.email} saiu da escola.`)
                          } catch (x) {
                            setErro(x instanceof ErroApi ? x.message : 'não foi possível remover')
                          }
                        }}
                      >
                        Remover
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dono && (
        <>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault()
              setErro(null)
              setLinkDoConvite(null)
              const email = emailNovo.trim()
              try {
                // Quem já tem conta entra direto; quem não tem precisa de
                // convite. A tela tenta o caminho curto e só cai no convite
                // quando o servidor diz que a pessoa ainda não existe.
                await apiEscola.adicionarMembro(projeto, email, papelNovo)
                setAviso(`${email} já tinha conta e entrou agora.`)
                setEmailNovo('')
                await carregar()
              } catch (x) {
                if (x instanceof ErroApi && x.status === 404) {
                  try {
                    const c = await apiEscola.convidar(projeto, email, papelNovo)
                    setLinkDoConvite(`${location.origin}/?convite=${c.token}`)
                    setEmailNovo('')
                    await carregar()
                  } catch (y) {
                    setErro(y instanceof ErroApi ? y.message : 'não foi possível convidar')
                  }
                } else {
                  setErro(x instanceof ErroApi ? x.message : 'não foi possível adicionar')
                }
              }
            }}
          >
            <label className="field">
              <span className="field__label">E-mail de quem entra</span>
              <input
                className="input"
                type="email"
                value={emailNovo}
                onChange={(e) => setEmailNovo(e.target.value)}
                required
              />
              <span className="field__hint">
                Se já tiver conta no Pauta, entra na hora. Se não tiver, sai um convite com link.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Como</span>
              <select
                className="select"
                value={papelNovo}
                onChange={(e) => setPapelNovo(e.target.value)}
              >
                {PAPEIS.map((p) => (
                  <option key={p.valor} value={p.valor}>
                    {p.rotulo} — {p.nota}
                  </option>
                ))}
              </select>
            </label>
            <div className="row">
              <button type="submit" className="btn btn--primary">
                Dar acesso
              </button>
            </div>
          </form>

          {linkDoConvite && (
            <p className="callout">
              <strong>Copie este link agora — ele não volta a aparecer.</strong> O servidor guarda
              só um resumo dele, como faz com as sessões. Mande pelo canal que você quiser; quem
              receber cria a própria senha.
              <br />
              <code className="mono">{linkDoConvite}</code>
            </p>
          )}

          {convites.filter((c) => !c.usado_em).length > 0 && (
            <>
              <div className="section__head">
                <h2>Convites à espera</h2>
                <p>emitidos e ainda não usados</p>
              </div>
              <div className="tabela-rolavel">
                <table className="tabela">
                  <thead>
                    <tr>
                      <th>E-mail</th>
                      <th>Papel</th>
                      <th>Vence</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {convites
                      .filter((c) => !c.usado_em)
                      .map((c) => (
                        <tr key={c.id}>
                          <td>{c.email}</td>
                          <td>{PAPEIS.find((p) => p.valor === c.papel)?.rotulo ?? c.papel}</td>
                          <td className="mono">{dia(c.expira_em)}</td>
                          <td className="tabela__acoes">
                            <button
                              type="button"
                              className="btn btn--ghost"
                              onClick={async () => {
                                await apiEscola.revogarConvite(projeto, c.id)
                                await carregar()
                                setAviso('Convite revogado. O link parou de funcionar.')
                              }}
                            >
                              Revogar
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="section__head">
            <h2>Excluir esta escola</h2>
            <p>some para todo mundo que participa dela</p>
          </div>
          <p className="callout" data-tour="excluir">
            <strong>Não há botão de desfazer nesta tela.</strong> O cadastro inteiro — turmas,
            professores, ofertas, disponibilidade e grades — sai da vista de todos os membros. O
            registro continua no banco, marcado com a hora da exclusão, porque o histórico de
            cada rodada do motor depende dele; recuperar, porém, exige quem administra o
            servidor. Para confirmar, digite <strong>{dados.nome}</strong> abaixo.
          </p>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault()
              try {
                await apiEscola.excluir(projeto)
                aoExcluir()
              } catch (x) {
                setErro(x instanceof ErroApi ? x.message : 'não foi possível excluir')
              }
            }}
          >
            <label className="field">
              <span className="field__label">Nome da escola</span>
              <input
                className="input"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                placeholder={dados.nome}
              />
            </label>
            <div className="row">
              <button
                type="submit"
                className="btn btn--danger"
                disabled={confirmacao.trim() !== dados.nome}
              >
                Excluir {dados.nome}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
